import React from 'react';
import { Document, Image, Link, Page, Path, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import { parseAsteriskBold } from '../utils/emphasis';
import bluwaveLogo from '../assets/BluWave-logo_final.png';
import bluwaveLogoWhite from '../assets/whiteBWLogo.png';
import PdfBannerBackdrop, { PDF_BANNER_HEIGHT } from './PdfBannerBackdrop';
import { BRAND_FONT_FAMILY, BRAND_GOLD, BRAND_PRIMARY } from '../utils/pdfBrand';
import { OVERVIEW_CARD_ID } from '../utils/pdfPageFit';

export type SnapshotBullet = { label: string; body: string };

const renderAsteriskBoldPdf = (input: string, keyPrefix: string): React.ReactNode[] => {
  const tokens = parseAsteriskBold(input ?? '');
  return tokens
    .map((t, idx) => {
      if (!t.value) return null;
      if (t.type === 'bold') {
        return (
          <Text key={`${keyPrefix}-b-${idx}`} style={{ fontWeight: 700 }}>
            {t.value}
          </Text>
        );
      }
      return (
        <Text key={`${keyPrefix}-t-${idx}`}>
          {t.value}
        </Text>
      );
    })
    .filter((n): n is JSX.Element => n !== null);
};

export type ProjectCriteriaFitRow = {
  label: string;
  yourNeeds: string;
  providerFit: string;
  providerFitHtml?: string;
};

export type RelevantProjectExperienceItem = {
  title: string;
  content: string;
};

export type TopSupportingEvidencePdfItem = {
  title: string;
  content: string;
};

export type OnePagerPdfProps = {
  headerBackgroundSrc?: string;
  logoSrc?: string;
  initials?: string;
  isVerified?: boolean;
  companyName: string;
  location: string;
  websiteText: string;

  projectOverviewTitle: string;
  projectOverviewHtml: string;

  snapshotTitle: string;
  snapshotBullets: SnapshotBullet[];
  snapshotHtml?: string;

  whyWeTitle: string;
  whyWeHtml?: string;

  trustedByTitle: string;
  trustedByFirmNames: string[];
  peBackedReviewQuote: string;
  peBackedReviewAttribution: string;

  topSupportingEvidenceTitle: string;
  topSupportingEvidenceItems: TopSupportingEvidencePdfItem[];

  projectCriteriaFitTitle: string;
  projectCriteriaFitRows: ProjectCriteriaFitRow[];

  relevantProjectExperienceTitle: string;
  relevantProjectExperienceItems: RelevantProjectExperienceItem[];

  magicLinkUrl?: string;
  inviteTeamMemberUrl?: string;

  // Scale factor (MIN_OVERVIEW_SCALE..1] applied to the Project Overview card's
  // typography and padding so long overviews can shrink to fit page 1.
  overviewScale?: number;
  // Receives react-pdf's internal layout tree after render; renderPdfFittedToPageOne
  // uses it to measure where the overview card actually landed.
  onLayout?: (layout: unknown) => void;
};

type InlineStyle = {
  fontWeight?: number;
  fontStyle?: 'italic';
  textDecoration?: 'underline' | 'line-through' | 'none';
  textDecorationStyle?: 'solid' | 'double' | 'dotted' | 'dashed' | 'wavy';
  textDecorationColor?: string;
  borderBottomWidth?: number;
  borderBottomColor?: string;
  borderBottomStyle?: 'solid';
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 32,
    paddingBottom: 70,
    paddingHorizontal: 36,
    fontSize: 11,
    color: '#111827',
    // Body copy stays sans (per the 2026 redesign); headings use Roboto Slab.
    fontFamily: 'Helvetica',
  },
  section: {
    marginBottom: 18,
  },
  sectionLast: {
    marginBottom: 0,
  },
  // Full-bleed banner (BPP-231): negative margins cancel the page padding so the
  // backdrop runs edge to edge; content keeps the page's 36pt horizontal rhythm.
  headerBanner: {
    position: 'relative',
    marginTop: -32,
    marginHorizontal: -36,
    marginBottom: 16,
    height: PDF_BANNER_HEIGHT,
    backgroundColor: '#0C1930',
    paddingTop: 20,
    paddingHorizontal: 36,
  },
  headerBrandRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerBrandLogo: {
    height: 16,
    width: 84,
    objectFit: 'contain',
  },
  headerNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerLogoCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerLogoImage: {
    width: 26,
    height: 18,
    objectFit: 'contain',
  },
  headerInitials: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
  },
  headerCompanyName: {
    fontSize: 22,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: '#FFFFFF',
    textAlign: 'left',
    maxLines: 1,
    textOverflow: 'ellipsis',
  },
  bannerChipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 14,
  },
  bannerChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    // Solid white: react-pdf renders rgba() border colors with a green cast.
    borderColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  bannerChipText: {
    fontSize: 10,
    fontWeight: 500,
    color: '#FFFFFF',
  },
  verifiedBadgeWrap: {
    width: 16,
    height: 16,
  },
  h1: {
    fontSize: 18,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
    marginBottom: 6,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  metaPill: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    paddingVertical: 4,
    paddingHorizontal: 8,
    fontSize: 10,
    color: '#374151',
  },
  metaPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaIcon: {
    width: 10,
    height: 10,
  },
  h2: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
    marginBottom: 8,
  },
  h3: {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
    marginBottom: 6,
  },
  h4: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
    marginBottom: 6,
  },
  link: {
    color: '#53BEE8',
    textDecoration: 'underline',
  },
  projectOverviewTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    marginBottom: 8,
    paddingLeft: 3,
  },
  projectOverviewCard: {
    backgroundColor: '#DCEDF7',
    borderRadius: 12,
    paddingTop: 14,
    paddingBottom: 12,
    paddingHorizontal: 16,
  },
  sparkleWrap: {
    width: 16,
    height: 16,
  },
  snapshotTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  snapshotIconWrap: {
    position: 'relative',
    width: 16,
    height: 16,
  },
  snapshotUserIcon: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 16,
    height: 16,
  },
  snapshotStarIcon: {
    position: 'absolute',
    right: -1,
    bottom: -1,
    width: 8,
    height: 8,
  },
  snapshotTitleText: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
  },
  paragraph: {
    fontSize: 11,
    // Tightened from 1.35 so long project overviews fit page 1 more often (BPP-215 follow-up).
    lineHeight: 1.25,
    marginBottom: 6,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  bulletDot: {
    width: 10,
    fontSize: 11,
  },
  bulletText: {
    flex: 1,
    fontSize: 11,
    lineHeight: 1.25,
  },
  trustedByCard: {
    backgroundColor: BRAND_PRIMARY,
    borderRadius: 10,
    paddingVertical: 16,
    paddingHorizontal: 20,
  },
  trustedByLabel: {
    fontSize: 9,
    color: BRAND_GOLD,
    marginBottom: 8,
  },
  firmNamesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 18,
  },
  firmNameText: {
    fontSize: 12,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: '#FFFFFF',
  },
  trustedByDivider: {
    height: 1,
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.25)',
    marginTop: 14,
    marginBottom: 12,
  },
  quoteText: {
    fontSize: 11,
    fontStyle: 'italic',
    lineHeight: 1.35,
    marginBottom: 4,
    color: '#FFFFFF',
  },
  quoteAttribution: {
    fontSize: 10,
    lineHeight: 1.35,
    color: 'rgba(255,255,255,0.8)',
  },
  topEvidenceHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  topEvidenceTitle: {
    fontSize: 13,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    lineHeight: 1.2,
    color: BRAND_PRIMARY,
  },
  evidenceCardsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginTop: 10,
    // The rule under the evidence cards is a border rather than a sibling <View>: a 2pt
    // standalone node can be orphaned onto its own page, and once it is, react-pdf sees a
    // non-fixed element ahead of the next section and stops suppressing that section's
    // minPresenceAhead break — leaving a page holding nothing but the hairline.
    paddingBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: '#E8E8E8',
  },
  evidenceCard: {
    flex: 1,
    backgroundColor: '#DCEDF7',
    borderRadius: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  evidenceCardTitle: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    lineHeight: 1.25,
    color: BRAND_PRIMARY,
  },
  evidenceCardBody: {
    marginTop: 6,
    fontSize: 11,
    lineHeight: 1.35,
    color: '#111827',
  },
  divider: {
    marginTop: 12,
    height: 2,
    width: '100%',
    backgroundColor: '#E8E8E8',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 6,
    marginBottom: 10,
  },
  tableHeaderCell: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
  },
  tableRow: {
    flexDirection: 'row',
    paddingBottom: 10,
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  criteriaHeader: {
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderBottomColor: BRAND_PRIMARY,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginBottom: 6,
  },
  criteriaRow: {
    flexDirection: 'row',
    paddingBottom: 6,
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  criteriaParagraph: {
    fontSize: 10,
    lineHeight: 1.25,
    marginBottom: 4,
  },
  cellLabel: {
    width: 110,
    paddingRight: 8,
  },
  cellNeeds: {
    flex: 1.35,
    paddingRight: 10,
  },
  cellArrow: {
    width: 24,
    alignItems: 'center',
    paddingTop: 2,
  },
  cellFit: {
    flex: 1.35,
  },
  cellLabelText: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: BRAND_FONT_FAMILY,
    color: BRAND_PRIMARY,
    lineHeight: 1.1,
  },
  criteriaLabelRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  criteriaIndexCircle: {
    width: 14,
    height: 14,
    borderRadius: 999,
    backgroundColor: BRAND_GOLD,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  criteriaIndexText: {
    fontSize: 8,
    fontWeight: 700,
    color: '#FFFFFF',
    lineHeight: 1,
  },
  criteriaArrowCircle: {
    width: 16,
    height: 16,
    borderRadius: 999,
    backgroundColor: '#EEE9DC',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: -10,
  },
  criteriaArrowText: {
    fontSize: 10,
    fontWeight: 700,
    color: BRAND_GOLD,
    lineHeight: 1,
    marginTop: -1,
  },
  criteriaArrowSvg: {
    width: 12,
    height: 10,
  },
  footerWrap: {
    position: 'absolute',
    left: 36,
    right: 36,
    bottom: 20,
  },
  footerDivider: {
    width: '100%',
    height: 2,
    backgroundColor: '#D1D5DB',
  },
  footerLogoRow: {
    width: '100%',
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerDisclaimer: {
    fontSize: 7,
    color: '#9CA3AF',
    flex: 1,
    paddingRight: 12,
  },
  footerLogo: {
    height: 18,
    width: 90,
    objectFit: 'contain',
  },
});

const normalizeHtml = (raw: string) => {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  // If the string looks double-encoded (plain text with escaped HTML tags),
  // decode entities so DOMParser receives valid HTML
  if (trimmed.includes('&lt;') || trimmed.includes('&gt;')) {
    return trimmed
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }
  return trimmed;
};

const mergeInlineStyle = (base: InlineStyle, next: InlineStyle): InlineStyle => {
  const result: InlineStyle = {};
  const fw = next.fontWeight ?? base.fontWeight; if (fw != null) result.fontWeight = fw;
  const fs = next.fontStyle ?? base.fontStyle; if (fs != null) result.fontStyle = fs;
  const td = next.textDecoration ?? base.textDecoration; if (td != null) result.textDecoration = td;
  const tds = next.textDecorationStyle ?? base.textDecorationStyle; if (tds != null) result.textDecorationStyle = tds;
  const tdc = next.textDecorationColor ?? base.textDecorationColor; if (tdc != null) result.textDecorationColor = tdc;
  const bbw = next.borderBottomWidth ?? base.borderBottomWidth; if (bbw != null) result.borderBottomWidth = bbw;
  const bbc = next.borderBottomColor ?? base.borderBottomColor; if (bbc != null) result.borderBottomColor = bbc;
  const bbs = next.borderBottomStyle ?? base.borderBottomStyle; if (bbs != null) result.borderBottomStyle = bbs;
  return result;
};

const computeInlineStyleForTag = (tagName: string): InlineStyle => {
  const t = tagName.toLowerCase();
  if (t === 'strong' || t === 'b') return { fontWeight: 700 };
  if (t === 'em' || t === 'i') return { fontStyle: 'italic' };
  if (t === 'u')
    return {
      textDecoration: 'underline',
      textDecorationStyle: 'solid',
      textDecorationColor: '#111827',
      borderBottomWidth: 0.75,
      borderBottomColor: '#111827',
      borderBottomStyle: 'solid',
    };
  return {};
};

const computeInlineStyleForElement = (el: Element): InlineStyle => {
  const tagStyle = computeInlineStyleForTag(el.tagName);

  const className = (el.getAttribute('class') ?? '').toLowerCase();
  const styleAttr = (el.getAttribute('style') ?? '').toLowerCase();

  const inline: InlineStyle = {};

  // Quill often uses spans with inline styles/classes instead of <u>
  if (className.includes('ql-underline')) {
    inline.textDecoration = 'underline';
    inline.textDecorationStyle = 'solid';
    inline.textDecorationColor = '#111827';
    inline.borderBottomWidth = 0.75;
    inline.borderBottomColor = '#111827';
    inline.borderBottomStyle = 'solid';
  }
  if (className.includes('ql-bold')) inline.fontWeight = 700;
  if (className.includes('ql-italic')) inline.fontStyle = 'italic';

  if (styleAttr.includes('text-decoration') && styleAttr.includes('underline')) {
    inline.textDecoration = 'underline';
    inline.textDecorationStyle = 'solid';
    inline.textDecorationColor = '#111827';
    inline.borderBottomWidth = 0.75;
    inline.borderBottomColor = '#111827';
    inline.borderBottomStyle = 'solid';
  }
  if (styleAttr.includes('font-style') && styleAttr.includes('italic')) inline.fontStyle = 'italic';
  if (styleAttr.includes('font-weight')) {
    if (styleAttr.includes('bold')) inline.fontWeight = 700;
    const m = styleAttr.match(/font-weight\s*:\s*(\d+)/);
    if (m?.[1]) {
      const n = Number(m[1]);
      if (!Number.isNaN(n)) inline.fontWeight = n;
    }
  }

  return mergeInlineStyle(tagStyle, inline);
};

const getTextFromNode = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const el = node as Element;
  if (el.tagName.toLowerCase() === 'br') return '\n';
  return Array.from(el.childNodes)
    .map((n) => getTextFromNode(n))
    .join('');
};

const getDirectListItems = (listEl: Element): Element[] => {
  const out: Element[] = [];
  Array.from(listEl.querySelectorAll('li')).forEach((li) => {
    if (li.parentElement === listEl) out.push(li);
  });
  return out;
};

const renderInlineNodes = (nodes: Node[], style: InlineStyle, keyPrefix: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  nodes.forEach((node, idx) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      if (text) {
        const tokens = parseAsteriskBold(text);
        tokens.forEach((t, tIdx) => {
          const value = t.value;
          if (!value) return;
          if (t.type === 'bold') {
            out.push(
              <Text key={`${keyPrefix}-t-${idx}-${tIdx}`} style={mergeInlineStyle(style, { fontWeight: 700 })}>
                {value}
              </Text>
            );
          } else if (style.fontStyle != null || style.fontWeight != null || style.textDecoration != null || style.borderBottomWidth != null) {
            // Active inline style — wrap in Text to apply it
            out.push(
              <Text key={`${keyPrefix}-t-${idx}-${tIdx}`} style={style}>
                {value}
              </Text>
            );
          } else {
            // No active style — push raw string so react-pdf renders it inline alongside
            // styled siblings. When every child is a <Text> node react-pdf v4 stacks them
            // as blocks; mixing raw strings triggers the correct inline flow.
            out.push(value);
          }
        });
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    const elementStyle = computeInlineStyleForElement(el);
    const nextStyle = mergeInlineStyle(style, elementStyle);

    // react-pdf underline can be unreliable when applied to nested <Text> nodes.
    // If an element results in an underline style, render it as a single leaf run
    // with the fully merged style (so bold+italic+underline compose).
    if ((nextStyle.textDecoration === 'underline' || nextStyle.borderBottomWidth) && tag !== 'a' && tag !== 'br') {
      const text = getTextFromNode(el);
      if (text) {
        out.push(
          <Text key={`${keyPrefix}-u-${idx}`} style={nextStyle}>
            {text}
          </Text>
        );
      }
      return;
    }

    if (tag === 'a') {
      const href = el.getAttribute('href') ?? '';
      const children = renderInlineNodes(Array.from(el.childNodes), nextStyle, `${keyPrefix}-a-${idx}`);
      if (!href) {
        out.push(
          <Text key={`${keyPrefix}-a-${idx}`} style={nextStyle}>
            {children}
          </Text>
        );
        return;
      }

      out.push(
        <Link key={`${keyPrefix}-a-${idx}`} src={href} style={{ ...(nextStyle as object), ...(styles.link as object) }}>
          {children}
        </Link>
      );
      return;
    }

    if (tag === 'br') {
      out.push(
        <Text key={`${keyPrefix}-br-${idx}`} style={nextStyle}>
          {'\n'}
        </Text>
      );
      return;
    }

    // Preserve nested markup (don't flatten to plain text), so combined formatting survives.
    // Block-level elements are handled by the top-level renderer; if we encounter them here,
    // treat them as containers.
    if (tag === 'p' || tag === 'div' || tag === 'li' || tag === 'ul' || tag === 'ol') {
      out.push(
        <Text key={`${keyPrefix}-blk-${idx}`} style={nextStyle}>
          {renderInlineNodes(Array.from(el.childNodes), nextStyle, `${keyPrefix}-blk-${idx}`)}
        </Text>
      );
      return;
    }

    // Avoid wrapping inline elements in an extra <Text> box — react-pdf treats each nested
    // <Text> as a separate inline run, and the redundant wrapper introduces whitespace
    // artifacts around formatted text. Push the children directly with the merged style.
    out.push(...renderInlineNodes(Array.from(el.childNodes), nextStyle, `${keyPrefix}-${idx}`));
  });
  return out;
};

const renderRichHtml = (html: string, scale = 1): React.ReactNode[] => {
  const normalized = normalizeHtml(html);
  if (!normalized) return [];

  // Body styles shrink with the fit scale; headings keep their brand sizes.
  const paragraphStyle = scale === 1
    ? styles.paragraph
    : { ...styles.paragraph, fontSize: 11 * scale, marginBottom: 6 * scale };
  const bulletDotStyle = scale === 1
    ? styles.bulletDot
    : { ...styles.bulletDot, width: 10 * scale, fontSize: 11 * scale };
  const bulletTextStyle = scale === 1
    ? styles.bulletText
    : { ...styles.bulletText, fontSize: 11 * scale };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(normalized, 'text/html');
    const body = doc.body;

    // Block-level tags that should each become their own block in the output.
    // Anything else (em, strong, span, a, br, etc.) is treated as inline and
    // grouped with adjacent text nodes into a single paragraph.
    const BLOCK_LEVEL_TAGS = new Set(['p', 'div', 'ul', 'ol', 'section', 'article', 'blockquote', 'header', 'footer', 'main', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'table']);
    const blocks: Element[] = [];
    let pendingInline: Node[] = [];
    const flushPending = () => {
      if (pendingInline.length === 0) return;
      const p = doc.createElement('p');
      pendingInline.forEach((n) => p.appendChild(n.cloneNode(true)));
      blocks.push(p);
      pendingInline = [];
    };
    Array.from(body.childNodes).forEach((n) => {
      if (n.nodeType === Node.ELEMENT_NODE) {
        const tag = (n as Element).tagName.toLowerCase();
        if (BLOCK_LEVEL_TAGS.has(tag)) {
          flushPending();
          blocks.push(n as Element);
        } else {
          pendingInline.push(n);
        }
      } else if (n.nodeType === Node.TEXT_NODE) {
        if ((n.textContent ?? '').trim()) pendingInline.push(n);
      }
    });
    flushPending();

    const out: React.ReactNode[] = [];
    blocks.forEach((el, idx) => {
      const tag = el.tagName.toLowerCase();

      if (tag === 'ul' || tag === 'ol') {
        const lis = getDirectListItems(el);
        let orderedCount = 0;

        lis.forEach((li, liIdx) => {
          const isOrdered = tag === 'ol' || li.getAttribute('data-list') === 'ordered';
          if (isOrdered) orderedCount += 1;
          const marker = isOrdered ? `${orderedCount}. ` : '• ';

          out.push(
            <View key={`whywe-li-${idx}-${liIdx}`} style={styles.bulletRow}>
              <Text style={bulletDotStyle}>{marker}</Text>
              <Text style={bulletTextStyle}>
                {renderInlineNodes(Array.from(li.childNodes), {}, `whywe-li-${idx}-${liIdx}`)}
              </Text>
            </View>
          );
        });
        return;
      }

      if (tag === 'h1') {
        out.push(
          <View key={`whywe-h1-${idx}`}>
            <Text style={styles.h1}>
              {renderInlineNodes(Array.from(el.childNodes), {}, `whywe-h1-${idx}`)}
            </Text>
          </View>
        );
        return;
      }

      if (tag === 'h2') {
        out.push(
          <View key={`whywe-h2-${idx}`}>
            <Text style={styles.h2}>
              {renderInlineNodes(Array.from(el.childNodes), {}, `whywe-h2-${idx}`)}
            </Text>
          </View>
        );
        return;
      }

      if (tag === 'h3') {
        out.push(
          <View key={`whywe-h3-${idx}`}>
            <Text style={styles.h3}>
              {renderInlineNodes(Array.from(el.childNodes), {}, `whywe-h3-${idx}`)}
            </Text>
          </View>
        );
        return;
      }

      if (tag === 'h4' || tag === 'h5' || tag === 'h6') {
        out.push(
          <View key={`whywe-h4-${idx}`}>
            <Text style={styles.h4}>
              {renderInlineNodes(Array.from(el.childNodes), {}, `whywe-h4-${idx}`)}
            </Text>
          </View>
        );
        return;
      }

      // For block elements (p, div, etc.) process their children as inline content.
      // For inline elements that appear at the top level without a block wrapper
      // (e.g. AI-generated content), pass the element itself so its style is preserved.
      const BLOCK_TAGS = new Set(['p', 'div', 'section', 'article', 'blockquote', 'header', 'footer', 'main']);
      const nodes = BLOCK_TAGS.has(tag) ? Array.from(el.childNodes) : [el as Node];
      out.push(
        <View key={`whywe-p-${idx}`}>
          <Text style={paragraphStyle}>
            {renderInlineNodes(nodes, {}, `whywe-p-${idx}`)}
          </Text>
        </View>
      );
    });

    return out;
  } catch {
    const stripped = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return [
      <View key="whywe-fallback">
        <Text style={paragraphStyle}>
          {stripped}
        </Text>
      </View>,
    ];
  }
};

export default function OnePagerPdf(props: OnePagerPdfProps) {
  const toTwoLineLabel = (label: string) => {
    const parts = label
      .split(/\s+/)
      .map((p) => p.trim())
      .filter((p) => !!p);
    if (parts.length <= 1) return label;
    const mid = Math.ceil(parts.length / 2);
    return `${parts.slice(0, mid).join(' ')}\n${parts.slice(mid).join(' ')}`;
  };

  const sparkleRotationDeg = 31;

  const overviewScale = Math.min(1, Math.max(0.5, props.overviewScale ?? 1));
  const overviewCardStyle = overviewScale === 1
    ? styles.projectOverviewCard
    : {
        ...styles.projectOverviewCard,
        paddingTop: 14 * overviewScale,
        paddingBottom: 12 * overviewScale,
        paddingHorizontal: 16 * overviewScale,
      };
  const overviewTitleRowStyle = overviewScale === 1
    ? styles.projectOverviewTitleRow
    : { ...styles.projectOverviewTitleRow, marginBottom: 8 * overviewScale };
  const handleDocumentRender = props.onLayout
    ? (params: unknown) => {
        props.onLayout?.((params as { _INTERNAL__LAYOUT__DATA_?: unknown })._INTERNAL__LAYOUT__DATA_);
      }
    : undefined;

  // The Project Criteria Fit heading and its column-header row. Rendered once, then kept
  // together with the first criteria row so the heading can't be stranded at a page break
  // (BPP-380).
  const criteriaTitleAndHeaders = (
    <>
      <Text style={[styles.h2, { marginBottom: 20 }]}>{props.projectCriteriaFitTitle}</Text>

      <View style={styles.criteriaHeader}>
        <View style={styles.cellLabel}>
          <Text style={styles.tableHeaderCell}>Criteria</Text>
        </View>
        <View style={styles.cellNeeds}>
          <Text style={styles.tableHeaderCell}>Your Project Needs</Text>
        </View>
        <View style={styles.cellArrow} />
        <View style={styles.cellFit}>
          <Text style={styles.tableHeaderCell}>Provider Fit</Text>
        </View>
      </View>
    </>
  );

  return (
    <Document onRender={handleDocumentRender}>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.footerWrap} fixed>
          <View style={styles.footerDivider} />
          <View style={styles.footerLogoRow}>
            <Text style={styles.footerDisclaimer}>
              Derived from BluWave's knowledge of the provider. Not liable for inaccuracies — independent verification recommended.
            </Text>
            <Image src={bluwaveLogo} style={styles.footerLogo} />
          </View>
        </View>

        <View style={styles.headerBanner} wrap={false}>
          <PdfBannerBackdrop />

          <View style={styles.headerBrandRow}>
            <Image src={bluwaveLogoWhite} style={styles.headerBrandLogo} />
            {(!!props.magicLinkUrl || !!props.inviteTeamMemberUrl) && (
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {!!props.inviteTeamMemberUrl && (
                  <Link src={props.inviteTeamMemberUrl} style={{ textDecoration: 'none' }}>
                    <View style={{ borderWidth: 1, borderColor: '#FFFFFF', borderRadius: 6, paddingVertical: 8, paddingHorizontal: 20 }}>
                      <Text style={{ color: '#FFFFFF', fontSize: 11, fontWeight: 700 }}>Invite Team Member</Text>
                    </View>
                  </Link>
                )}
                {!!props.magicLinkUrl && (
                  <Link src={props.magicLinkUrl} style={{ textDecoration: 'none' }}>
                    <View style={{ backgroundColor: '#FFFFFF', borderRadius: 6, paddingVertical: 8, paddingHorizontal: 20 }}>
                      <Text style={{ color: '#132543', fontSize: 11, fontWeight: 700 }}>Get Introduced</Text>
                    </View>
                  </Link>
                )}
              </View>
            )}
          </View>

          <View style={styles.headerNameRow}>
            <View style={styles.headerLogoCircle}>
              {!props.logoSrc && <Text style={styles.headerInitials}>{props.initials || ''}</Text>}
              {!!props.logoSrc && <Image src={props.logoSrc} style={styles.headerLogoImage} />}
            </View>
            <Text style={styles.headerCompanyName}>{props.companyName || 'One-Pager'}</Text>
            {!!props.isVerified && (
              <View style={styles.verifiedBadgeWrap}>
                <Svg width="16" height="16" viewBox="0 0 30 30">
                  <Path
                    d="M15 1.5L18.6 4.4L23.2 3.9L24.1 8.4L28 11L25.7 15L28 19L24.1 21.6L23.2 26.1L18.6 25.6L15 28.5L11.4 25.6L6.8 26.1L5.9 21.6L2 19L4.3 15L2 11L5.9 8.4L6.8 3.9L11.4 4.4L15 1.5Z"
                    fill="#FFFFFF"
                  />
                  <Path
                    d="M12.8 15.2L14.3 16.7L18 13"
                    stroke="#132543"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
              </View>
            )}
          </View>

          <View style={styles.bannerChipRow}>
            {!!props.location && (
              <View style={styles.bannerChip}>
                <Svg style={styles.metaIcon} viewBox="0 0 24 24">
                  <Path
                    d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <Path
                    d="M12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                    fill="none"
                    stroke="#FFFFFF"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </Svg>
                <Text style={styles.bannerChipText}>{props.location}</Text>
              </View>
            )}
            {!!props.websiteText && (
              <Link
                src={props.websiteText.startsWith('http') ? props.websiteText : `https://${props.websiteText}`}
                style={{ textDecoration: 'none' }}
              >
                <View style={styles.bannerChip}>
                  <Svg style={styles.metaIcon} viewBox="0 0 24 24">
                    <Path
                      d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Z"
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M2 12h20"
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <Path
                      d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z"
                      fill="none"
                      stroke="#FFFFFF"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </Svg>
                  <Text style={styles.bannerChipText}>Website</Text>
                </View>
              </Link>
            )}
          </View>
        </View>

        <View style={styles.section} minPresenceAhead={50}>
          <View id={OVERVIEW_CARD_ID} style={overviewCardStyle}>
            <View style={overviewTitleRowStyle} wrap={false}>
              <View style={styles.sparkleWrap}>
                <Svg width="16" height="16" viewBox="0 0 24 24">
                  <Path
                    d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z"
                    fill="none"
                    stroke="#132543"
                    strokeWidth={2}
                    strokeLinejoin="round"
                    transform={`rotate(${sparkleRotationDeg} 12 12)`}
                  />
                </Svg>
              </View>
              <Text style={{ ...styles.h2, marginBottom: 0 }}>{props.projectOverviewTitle}</Text>
            </View>
            {props.projectOverviewHtml ? renderRichHtml(props.projectOverviewHtml, overviewScale) : null}
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.snapshotTitleRow} wrap={false}>
            <View style={styles.snapshotIconWrap}>
              <Svg style={styles.snapshotUserIcon} viewBox="0 0 24 24">
                <Path
                  d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5z"
                  fill="none"
                  stroke="#132543"
                  strokeWidth={2}
                />
                <Path
                  d="M4 22c0-4.418 3.582-8 8-8s8 3.582 8 8"
                  fill="none"
                  stroke="#132543"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              </Svg>
              <Svg style={styles.snapshotStarIcon} viewBox="0 0 24 24">
                <Path
                  d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1 3-6z"
                  fill="#132543"
                  stroke="#132543"
                  strokeWidth={1}
                  strokeLinejoin="round"
                />
              </Svg>
            </View>
            <Text style={styles.snapshotTitleText}>{props.snapshotTitle}</Text>
          </View>
          {props.snapshotHtml
            ? renderRichHtml(props.snapshotHtml)
            : props.snapshotBullets.map((b, i) => (
                <View key={`${b.label}-${i}`} style={styles.bulletRow} wrap={false}>
                  <Text style={styles.bulletDot}>{'•'}</Text>
                  <Text style={styles.bulletText}>
                    <Text style={{ fontWeight: 700 }}>{`${b.label}: `}</Text>
                    {b.body}
                  </Text>
                </View>
              ))}
        </View>

        <View style={[styles.section, { marginTop: 14 }]} minPresenceAhead={140}>
          <Text style={styles.h2}>{props.whyWeTitle}</Text>
          {props.whyWeHtml ? renderRichHtml(props.whyWeHtml) : null}
        </View>

        <View style={styles.section} minPresenceAhead={140}>
          <View style={styles.trustedByCard} wrap={false}>
            <Text style={styles.trustedByLabel}>{props.trustedByTitle}</Text>
            {props.trustedByFirmNames.length > 0 && (
              <View style={styles.firmNamesRow}>
                {props.trustedByFirmNames.slice(0, 6).map((name, idx) => (
                  <Text key={`${name}-${idx}`} style={styles.firmNameText}>
                    {name}
                  </Text>
                ))}
              </View>
            )}
            <View style={styles.trustedByDivider} />
            <Text style={styles.trustedByLabel}>PE-backed review</Text>
            <Text style={styles.quoteText}>{props.peBackedReviewQuote}</Text>
            <Text style={styles.quoteAttribution}>{props.peBackedReviewAttribution}</Text>
          </View>
        </View>

        {props.topSupportingEvidenceItems.length > 0 && (
          <View style={styles.section}>
            <View style={styles.topEvidenceHeaderRow} wrap={false}>
              <Text style={styles.topEvidenceTitle}>{props.topSupportingEvidenceTitle}</Text>
            </View>

            <View style={styles.evidenceCardsRow}>
              {props.topSupportingEvidenceItems.slice(0, 2).map((item, idx) => (
                <View key={`evidence-card-${idx}`} style={styles.evidenceCard}>
                  <Text style={styles.evidenceCardTitle}>
                    {renderAsteriskBoldPdf(item.title, `evidence-title-${idx}`)}
                  </Text>
                  <View style={{ marginTop: 6 }}>
                    {renderRichHtml(item.content)}
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/*
          No `minPresenceAhead` on this container: on a section taller than a page it never
          fires (react-pdf only consults it when the node does *not* need splitting), and when
          the table is short enough to fit it throws the whole section onto the next page,
          stranding whatever short block preceded it. Heading orphans are handled by keeping
          the heading + column headers in the same wrap={false} block as the first row below.

          The column-header row is deliberately NOT react-pdf `fixed`: `fixed` repeats a node
          on *every* page of the document (verified on v4), which would print the headers above
          the banner on page 1. Repeating the header on continuation pages only would need a
          manual pagination pass. (BPP-380)
        */}
        <View
          style={
            props.relevantProjectExperienceItems.length > 0 ? styles.section : [styles.section, styles.sectionLast]
          }
        >
          {props.projectCriteriaFitRows.length === 0 ? (
            // No rows: the heading and headers still can't be allowed to end a page alone.
            <View wrap={false}>{criteriaTitleAndHeaders}</View>
          ) : (
            props.projectCriteriaFitRows.map((r, i) => {
              const row = (
                <View key={`${r.label}-${i}`} style={styles.criteriaRow} wrap={false}>
                  <View style={styles.cellLabel}>
                    <View style={styles.criteriaLabelRow}>
                      <View style={styles.criteriaIndexCircle}>
                        <Text style={styles.criteriaIndexText}>{i + 1}</Text>
                      </View>
                      <Text style={styles.cellLabelText}>{toTwoLineLabel(r.label)}</Text>
                    </View>
                  </View>
                  <View style={styles.cellNeeds}>
                    <Text style={styles.criteriaParagraph}>{r.yourNeeds}</Text>
                  </View>
                  <View style={styles.cellArrow}>
                    <View style={styles.criteriaArrowCircle}>
                      <Svg style={styles.criteriaArrowSvg} viewBox="0 0 24 24">
                        <Path
                          d="M5 12h14M13 5l7 7-7 7"
                          stroke={BRAND_GOLD}
                          strokeWidth={2.25}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </Svg>
                    </View>
                  </View>
                  <View style={styles.cellFit}>
                    {r.providerFitHtml ? (
                      <View>
                        {renderRichHtml(r.providerFitHtml)}
                      </View>
                    ) : (
                      <Text style={styles.criteriaParagraph}>{r.providerFit}</Text>
                    )}
                  </View>
                </View>
              );

              if (i > 0) return row;

              // Keep the heading + column headers with the first row so the heading can't be
              // orphaned at the bottom of a page; if they don't fit, they move together.
              return (
                <View key="criteria-heading" wrap={false}>
                  {criteriaTitleAndHeaders}
                  {row}
                </View>
              );
            })
          )}
        </View>

        {props.relevantProjectExperienceItems.length > 0 && (
          // Same reasoning as the criteria section: `minPresenceAhead` here measured the gap
          // *after* the section, so a section that fitted the page but left less than 140pt
          // beneath it was moved wholesale to the next page. See the note above.
          <View style={styles.section}>
            {props.relevantProjectExperienceItems.map((item, i, arr) => {
              const entry = (
                <View key={`${item.title}-${i}`} style={{ marginBottom: i < arr.length - 1 ? 10 : 0 }} wrap={false}>
                  <Text style={{ fontSize: 11, fontWeight: 700, fontFamily: BRAND_FONT_FAMILY, color: BRAND_PRIMARY, marginBottom: 4 }}>{item.title}</Text>
                  <View>{renderRichHtml(item.content)}</View>
                </View>
              );
              if (i > 0) return entry;
              // Keep the heading with the first entry rather than letting it end a page alone.
              return (
                <View key="relevant-experience-heading" wrap={false}>
                  <Text style={styles.h2}>{props.relevantProjectExperienceTitle}</Text>
                  {entry}
                </View>
              );
            })}
          </View>
        )}

        <View style={[styles.section, styles.sectionLast]}>
          <View style={styles.divider} />
          <Text style={{ fontSize: 9, fontWeight: 700, color: '#6B7280', marginTop: 8, marginBottom: 4 }}>Disclaimer</Text>
          <Text style={{ fontSize: 7, color: '#9CA3AF', lineHeight: 1.4 }}>
            STRICTLY CONFIDENTIAL. PROPRIETARY TO BLUWAVE, LP. INFORMATION HEREIN IS STRICTLY CONFIDENTIAL. BLUWAVE, LP DOES NOT MAKE ANY WARRANTY AS TO THE ACCURACY OF THE INFORMATION HEREIN AND/OR RESULTS THAT MAY BE OBTAINED FROM USE OF THIS SERVICE PROVIDER, OR ANY REFERRAL, EXCEPT AS EXPRESSLY SET FORTH IN A DEFINITIVE BLUWAVE SERVICES AGREEMENT. ANY REFERRALS ARE PROVIDED &quot;AS IS&quot; AND BLUWAVE, LP DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT.
          </Text>
        </View>
      </Page>
    </Document>
  );
}
